import React, { useMemo, useState } from 'react';
import { Table, Space, Input, Button, Spin, Alert } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import {
  SpaceAssignment,
  SpaceType,
  SpaceRelation,
  getAccessLevelDisplay,
  getSpaceLevelDisplay,
  GET_USER_SPACE_ASSIGNMENTS,
  GetUserSpaceAssignmentsResponse,
  GetUserSpaceAssignmentsVariables,
  AUTH_API_URL,
} from './SpaceAssignments';
import { getSpaceLevelIcon } from './SpaceIcons';
import { GET_SITE_HIERARCHY } from '@/hooks/useSiteOptions';
import {
  SiteOption,
  BuildingOption,
  FloorOption,
  RoomOption,
} from '@/hooks/useSiteHierarchyFilters';

interface DisplayRow {
  key: string;
  spacePath: string;
  spaceName: string;
  space_type: SpaceType;
  relation: SpaceRelation;
  space_id: string;
}

/**
 * Build a lookup map from the site hierarchy to resolve space IDs to names and breadcrumb paths.
 */
function buildSpaceLookup(sites: SiteOption[]) {
  const nameMap = new Map<string, string>();
  const pathMap = new Map<string, string>();

  for (const site of sites) {
    nameMap.set(site.id, site.name);
    pathMap.set(site.id, site.name);

    for (const building of site.buildings || []) {
      nameMap.set(building.id, building.name);
      pathMap.set(building.id, `${site.name} > ${building.name}`);

      for (const floor of (building as BuildingOption).floors || []) {
        nameMap.set(floor.id, floor.name);
        pathMap.set(floor.id, `${site.name} > ${building.name} > ${floor.name}`);

        for (const room of (floor as FloorOption).rooms || []) {
          nameMap.set(room.id, room.name);
          pathMap.set(room.id, `${site.name} > ${building.name} > ${floor.name} > ${room.name}`);
        }

        for (const zone of (floor as FloorOption).zones || []) {
          const roomName = (floor.rooms || []).find((r: RoomOption) => r.id === zone.roomId)?.name;
          if (roomName) {
            nameMap.set(zone.id, zone.name);
            pathMap.set(
              zone.id,
              `${site.name} > ${building.name} > ${floor.name} > ${roomName} > ${zone.name}`,
            );
          } else {
            nameMap.set(zone.id, zone.name);
            pathMap.set(zone.id, `${site.name} > ${building.name} > ${floor.name} > ${zone.name}`);
          }
        }
      }
    }
  }

  return { nameMap, pathMap };
}

/**
 * Derive the user ID from the Auth0 sub claim.
 * Must match the conversion used by SpaceAccessContext (convertAuth0IdToUserId).
 * e.g. "auth0|user_2rPF..." → "user_2rPF..." or "auth0|abc123" → "user_abc123"
 */
function deriveUserId(sub: string | undefined): string | null {
  if (!sub) return null;
  const parts = sub.split('|');
  if (parts.length !== 2) return null;
  const idPart = parts[1];
  return idPart.startsWith('user_') ? idPart : 'user_' + idPart;
}

export const AccessPage: React.FC = () => {
  const { user } = useAuth0();
  const userId = deriveUserId(user?.sub);

  const [searchText, setSearchText] = useState('');

  // Fetch space assignments for the current user
  const {
    data: assignmentsData,
    loading: assignmentsLoading,
    error: assignmentsError,
    refetch,
  } = useQuery<GetUserSpaceAssignmentsResponse, GetUserSpaceAssignmentsVariables>(
    GET_USER_SPACE_ASSIGNMENTS,
    {
      variables: { user_id: userId || '' },
      context: { uri: AUTH_API_URL },
      skip: !userId,
    },
  );

  // Fetch site hierarchy to resolve space names and build breadcrumb paths
  const { data: hierarchyData, loading: hierarchyLoading } = useQuery(GET_SITE_HIERARCHY);

  const { nameMap, pathMap } = useMemo(() => {
    const sites = (hierarchyData?.sites?.data || []) as SiteOption[];
    return buildSpaceLookup(sites);
  }, [hierarchyData]);

  const displayRows: DisplayRow[] = useMemo(() => {
    const assignments = assignmentsData?.userSpaceAssignments || [];
    return assignments.map((a: SpaceAssignment) => ({
      key: `${a.space_type}-${a.space_id}-${a.relation}`,
      spacePath: pathMap.get(a.space_id) || a.space_id,
      spaceName: nameMap.get(a.space_id) || a.space_id,
      space_type: a.space_type,
      relation: a.relation,
      space_id: a.space_id,
    }));
  }, [assignmentsData, nameMap, pathMap]);

  const filteredRows = useMemo(() => {
    if (!searchText) return displayRows;
    const lower = searchText.toLowerCase();
    return displayRows.filter(
      (row) =>
        row.spacePath.toLowerCase().includes(lower) ||
        row.spaceName.toLowerCase().includes(lower) ||
        getSpaceLevelDisplay(row.space_type).toLowerCase().includes(lower) ||
        getAccessLevelDisplay(row.relation).toLowerCase().includes(lower),
    );
  }, [displayRows, searchText]);

  const handleDownload = () => {
    const header = 'Assigned Space,Level,Role';
    const rows = filteredRows.map(
      (row) =>
        `"${row.spacePath}","${getSpaceLevelDisplay(row.space_type)}","${getAccessLevelDisplay(
          row.relation,
        )}"`,
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'my-access.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const isLoading = assignmentsLoading || hierarchyLoading;

  const columns = [
    {
      title: 'Assigned Space',
      dataIndex: 'spacePath',
      key: 'spacePath',
      width: '33.33%',
      render: (path: string, record: DisplayRow) => (
        <Space>
          {getSpaceLevelIcon(record.space_type)}
          <span className="font-medium">{path}</span>
        </Space>
      ),
    },
    {
      title: 'Level',
      dataIndex: 'space_type',
      key: 'space_type',
      width: '33.33%',
      render: (type: SpaceType) => getSpaceLevelDisplay(type),
    },
    {
      title: 'Role',
      dataIndex: 'relation',
      key: 'relation',
      width: '33.33%',
      render: (relation: SpaceRelation) => getAccessLevelDisplay(relation),
    },
  ];

  if (isLoading) {
    return (
      <div className="bg-gray-50 w-full min-h-screen flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (assignmentsError) {
    return (
      <div className="bg-gray-50 w-full min-h-screen p-6">
        <Alert
          message="Error loading access information"
          description="Failed to fetch your access information. Please try again."
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="bg-surface-sunken w-full min-h-screen p-6">
      <div className="bg-surface rounded p-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-4">
          <Input
            placeholder="Search spaces..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="max-w-xs"
            allowClear
          />
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleDownload}>
              Download
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredRows}
          rowKey="key"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} spaces`,
          }}
          locale={{ emptyText: 'No space assignments found' }}
        />
      </div>
    </div>
  );
};

export default AccessPage;
